/** @format */
'use client';
import LeaderboardTabView from '@/app/[locale]/(protected)/(dashboard)/ui/LeaderboardTabView';
import TabTitleRender from '@/app/[locale]/(protected)/(dashboard)/ui/TabTitleRender';
import { ICurrency } from '@/common/types/currency';
import { useConfigStore } from '@/providers/ConfigStoreProvider';
import FlexBox from '@atoms/FlexBox';
import { cssVars } from '@atoms/GlobalStyles';
import Link from '@atoms/Link';
import { CompanyClient } from '@common/model/apiClient';
import { Account } from '@common/model/apiClient/Account';
import { Branch } from '@common/model/apiClient/Branch';
import { User } from '@common/model/apiClient/User';
import { css } from '@emotion/react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useQuery } from '@tanstack/react-query';
import { TabPanel } from 'devextreme-react';
import React, { useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

interface CompanyLeaderboardProps {
  filterRange: { start: Date; end: Date };
}

const StyledCompanyLeaderboard = styled.div`
  //width: 100%;
  flex: 1;
  background: var(--grey-100);
  //padding: 8px;
  border-radius: 3px;
  height: 100%;
  //overflow: hidden;
`;

const Card = styled.div`
  padding: 8px;
  cursor: pointer;
  background: var(--grey-100);
  border-bottom: 1px solid var(--grey-100);
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: background 0.3s ease;
  :hover {
    background: ${cssVars.lightBlue};
  }
`;

const Count = styled.span`
  font-weight: bold;
  font-size: 1.1rem;
  color: var(--primary-500);
`;

const CardTitle = styled.div`
  font-size: 1.3rem;
  font-weight: bold;
  grid-area: 1 / 1 / 3 / 2;
  border-bottom: 1px solid var(--black);
  padding: 6px 4px;
  width: 75%;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.7rem;
  align-items: center;
  color: var(--grey-500);
`;

function CountBody(props: { docs: string; savings: string }) {
  return (
    <FlexBox
      $justifyContent={'space-between'}
      $alignItems={'center'}
      // width={'80%'}
      css={css`
        padding: 4px;
      `}
    >
      <CountWrapper>
        <Count>{props.savings}</Count>
        <span>
          <FormattedMessage defaultMessage={'Total Savings'} description={'Total value of all savings'} />
        </span>
      </CountWrapper>
      <CountWrapper>
        <Count>{props.docs} </Count>
        <span>
          <FormattedMessage defaultMessage="Documents" description="Account page documents header" />
        </span>
      </CountWrapper>
    </FlexBox>
  );
}

function AccountCard(props: { account: Account; intl: IntlShape; selectedCurrency: ICurrency }) {
  return (
    <Link href={`/account/${props.account.id}`}>
      <Card
      // onClick={() => {
      //   router.push(`/account/${props.account.id}`);
      // }}
      >
        <CardTitle>{props.account.name}</CardTitle>
        <CountBody
          docs={String(props.account.totalCount ?? 0)}
          savings={props.intl.formatNumber(Number(props.account.savingsTotal), { style: 'currency', currency: props.selectedCurrency?.iso4217 })}
        />
      </Card>
    </Link>
  );
}

const BranchCard = (props: { branch: Branch & { savingsTotal: string; totalCount: number }; intl: IntlShape; selectedCurrency: ICurrency }) => {
  return (
    <Link href={`/branch/${props.branch.id}`}>
      <Card
      // onClick={() => {
      //   router.push(`/account/${props.account.id}`);
      // }}
      >
        <CardTitle>{props.branch.name}</CardTitle>
        <CountBody
          docs={props.branch.totalCount.toString()}
          savings={props.intl.formatNumber(Number(props.branch.savingsTotal), { style: 'currency', currency: props.selectedCurrency?.iso4217 })}
        />
      </Card>
    </Link>
  );
};

function UserCard(props: { user; intl: IntlShape; selectedCurrency: ICurrency }) {
  const data = props.user;
  const user = data.author.hits.hits[0]._source.author;
  return (
    <Link href={`/user/${user?.userId}`} disabled={!user?.userId}>
      <Card>
        <CardTitle>
          {user.givenName} {user.familyName}
        </CardTitle>
        {/*<div>{user.email}</div>*/}
        <CountBody docs={data.doc_count} savings={props.intl.formatNumber(Number(data.totalSavings.value), { style: 'currency', currency: props.selectedCurrency?.iso4217 })} />
      </Card>
    </Link>
  );
}

function CompanyLeaderboard(props: CompanyLeaderboardProps) {
  const intl = useIntl();
  const selectedCurrency = useConfigStore((s) => s.currency);

  const { data, isLoading } = useQuery({
    queryKey: ['get_company_admin_rankings', props.filterRange],
    queryFn: () => CompanyClient.getRankings({ startDate: props.filterRange.start, endDate: props.filterRange.end }),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    // enabled: isAdmin && isAdminChecked,
  });

  const topAccounts: Array<Account> = data?.payload.topAccounts ?? [];
  const topUsers: Array<{ total_savings: { value: number }; author: { hits: Array<{ _source: User }> } }> = data?.payload.topUsers ?? [];
  const topBranches: Array<Branch> = data?.payload.topBranches ?? [];

  const rankingCategories = useMemo(
    () => [
      {
        id: 'topUsers',
        icon: 'users' as IconName,
        title: <FormattedMessage defaultMessage={'Top Users'} description={'Label for table of users who have created the most savings'} />,
        records: topUsers,
        render: (u) => <UserCard key={`topUser${u.author.hits.hits[0]._source.author.email}`} user={u} intl={intl} selectedCurrency={selectedCurrency} />,
        loading: isLoading,
      },
      {
        id: 'topAccounts',
        icon: 'chart-line' as IconName,
        title: <FormattedMessage defaultMessage={'Top Accounts'} description={'Label for table of accounts with most created savings'} />,
        records: topAccounts,
        render: (a: Account) => <AccountCard key={`topAccount${a.id}`} account={a} intl={intl} selectedCurrency={selectedCurrency} />,
        loading: isLoading,
      },
      {
        id: 'topBranches',
        icon: 'building' as IconName,
        title: <FormattedMessage defaultMessage={'Top Branches'} description={'Label for table of branches with most created savings'} />,
        records: topBranches,
        render: (b: Branch & { savingsTotal: string; totalCount: number }) => <BranchCard key={`topBranch${b.id}`} branch={b} intl={intl} selectedCurrency={selectedCurrency} />,
        loading: isLoading,
      },
    ],
    [topUsers, topAccounts, topBranches, isLoading, intl, selectedCurrency],
  );

  return (
    <StyledCompanyLeaderboard>
      <TabPanel dataSource={rankingCategories} itemTitleRender={TabTitleRender} itemComponent={LeaderboardTabView} animationEnabled={false} height={'100%'} />
    </StyledCompanyLeaderboard>
  );
}

export default CompanyLeaderboard;
